import { useCallback, useEffect, useState } from "react";
import {
	DECISION_KINDS,
	FEEDBACK_KINDS,
	reportIn,
	researchApiBase,
	researchIndexOf,
	type AnalysisEnvironment,
	type AnalysisInput,
	type AnalysisPlan,
	type AnalysisRun,
	type AnalysisSummary,
	type AnalysisView,
	type AnchorLocator,
	type ClaimSummary,
	type DecisionKind,
	type EvidenceImpacts,
	type EvidenceSummary,
	type ImpactItem,
	type QualitySummary,
	type RecordRead,
	type ResearchCapabilities,
	type ResearchMapView,
	type ResearchScopeRef,
	type ResearchView,
	type StoredRecord,
	type VerificationCheck,
	type VerificationFileRef,
	type VerificationReport,
	type VerificationSummary,
} from "../../../shared/wire/research.ts";
import {
	BOUNDARY_LEVEL_LABEL,
	CHECK_STATUS_LABEL,
	CHECK_TYPE_LABEL,
	CONTEXT_DISPATCH_LABEL,
	EXECUTION_MODE_LABEL,
	MODEL_OVERLAP_LABEL,
	RECEIPT_RELATION_LABEL,
	SIBLING_READ_LABEL,
	SOURCE_OVERLAP_LABEL,
} from "../../../shared/research/labels.ts";
import type { ToolResultMessage } from "../types";
import type { ComponentProps } from "./index";
import { DecisionDetail } from "./ResearchDecision";
import { textOf } from "../views/notices";
import { ResearchMapPanel } from "./ResearchMap";
import { blockedCopy } from "./researchMapView";
import { AnalysisSection } from "./ResearchAnalysis";
import { FeedbackFields, QualitySummaryCard } from "./ResearchFeedback";
import {
	anchorStatusLabel,
	authorityLabel,
	DOMAIN_LABELS,
	existenceLabel,
	formatLabel,
	impactStatusLabel,
	kindLabel,
	noticeLabel,
	readingLabel,
	recordStatusLabel,
	relationLabel,
	scopeLabel,
	sectionLabel,
	sourceStatusLabel,
	supportLabel,
	timeLabel,
} from "./researchLabels";

/** Fired after a research change made in this browser, so every research view on screen refetches. */
export const RESEARCH_CHANGED = "research:changed";

/** Asks the app to open a research page link in place; a cancelled event means it did. Its detail is the href. */
export const RESEARCH_OPEN = "research:open";

/** Opens a research page link in the app, unless the click asks the browser for a new tab or window. */
export function openInApp(event: React.MouseEvent, href: string) {
	if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
	if (!window.dispatchEvent(new CustomEvent(RESEARCH_OPEN, { detail: href, cancelable: true }))) event.preventDefault();
}

export type ResearchResult = { state: "running" } | { state: "failed"; message: string } | { state: "view"; view: ResearchView };

/** What a research tool's result gives the renderer: the server's view, or why there is none. */
export function readResearchResult(result: ToolResultMessage | undefined): ResearchResult {
	if (!result) return { state: "running" };
	const view = (result.details as { researchView?: ResearchView } | undefined)?.researchView;
	if (result.isError || !view) return { state: "failed", message: textOf(result.content) || "无结果" };
	return { state: "view", view };
}

async function getJson<T>(path: string): Promise<T> {
	const res = await fetch(path);
	const text = await res.text();
	const data = text ? (JSON.parse(text) as unknown) : null;
	if (!res.ok) throw new Error((data as { error?: string } | null)?.error ?? `HTTP ${res.status}`);
	return data as T;
}

/** One plan revision as the scope's API keeps it, with the plan's newest revision number. */
async function planRevision(base: string, id: string, revision: number): Promise<AnalysisPlan & { revisions: number }> {
	const { revisions } = await getJson<{ revisions: AnalysisPlan[] }>(`${base}/analysis/plans/${encodeURIComponent(id)}/history`);
	const plan = revisions.find((p) => p.revision === revision);
	if (!plan) throw new Error(`计划 ${id} r${revision} 不存在`);
	return { ...plan, revisions: Math.max(...revisions.map((p) => p.revision)) };
}

/** One analysis record of a kind as the analysis domain reads it now; a plan at the revision given. */
export async function analysisRecord(base: string, kind: string, id: string, revision = 0): Promise<AnalysisView> {
	const at = encodeURIComponent(id);
	const none: AnalysisView = { runs: [], plans: [], inputs: [], environments: [] };
	switch (kind) {
		case "run":
			return { ...none, runs: [(await getJson<{ run: AnalysisRun }>(`${base}/analysis/runs/${at}`)).run] };
		case "plan":
			return { ...none, plans: [await planRevision(base, id, revision)] };
		case "input":
			return { ...none, inputs: [(await getJson<{ input: AnalysisInput }>(`${base}/analysis/inputs/${at}`)).input] };
		case "environment":
			return { ...none, environments: [(await getJson<{ environment: AnalysisEnvironment }>(`${base}/analysis/environments/${at}`)).environment] };
		default:
			return none;
	}
}

/** The same analysis objects as the scope's API answers now; an environment check is not stored, so it stays. */
async function refetchAnalysis(base: string, analysis: AnalysisView): Promise<AnalysisView> {
	const reads = await Promise.all([
		...analysis.runs.map((r) => analysisRecord(base, "run", r.id)),
		...analysis.plans.map((p) => analysisRecord(base, "plan", p.id, p.revision)),
		...analysis.inputs.map((i) => analysisRecord(base, "input", i.id)),
		...analysis.environments.map((e) => analysisRecord(base, "environment", e.id)),
	]);
	return {
		...analysis,
		runs: reads.flatMap((r) => r.runs),
		plans: reads.flatMap((r) => r.plans),
		inputs: reads.flatMap((r) => r.inputs),
		environments: reads.flatMap((r) => r.environments),
	};
}

/** The view as the scope's API answers now; a result that changed nothing (unavailable, refused) comes back as it was. */
export async function refetchView(view: ResearchView): Promise<ResearchView> {
	const base = researchApiBase(view.scopeRef);
	const now = Date.now();
	switch (view.section) {
		case "analysis_summary": {
			const { runs, plans, inputs, environments, notices, revision, snapshotAt } = await getJson<AnalysisSummary>(`${base}/analysis/summary`);
			return { ...view, analysis: { runs, plans, inputs, environments }, notices, revision, snapshotAt };
		}
		case "plan_history": {
			const id = view.analysis?.plans[0]?.id;
			if (!id) return view;
			const { revisions } = await getJson<{ revisions: AnalysisPlan[] }>(`${base}/analysis/plans/${encodeURIComponent(id)}/history`);
			return { ...view, analysis: { runs: [], inputs: [], environments: [], plans: revisions }, snapshotAt: now };
		}
		case "analysis":
			return view.analysis ? { ...view, analysis: await refetchAnalysis(base, view.analysis), snapshotAt: now } : view;
		case "evidence_summary": {
			const evidence = await getJson<EvidenceSummary>(`${base}/evidence/summary`);
			return { ...view, evidence, notices: evidence.notices, revision: evidence.revision, snapshotAt: evidence.snapshotAt };
		}
		case "evidence_impacts": {
			const impacts = await getJson<EvidenceImpacts>(`${base}/evidence/impacts${view.query ? `?${new URLSearchParams(view.query).toString()}` : ""}`);
			return { ...view, impacts: impacts.paragraphs, notices: impacts.notices, revision: impacts.revision, snapshotAt: impacts.snapshotAt };
		}
		case "record":
		case "report": {
			const ids = [...new Set(view.records.map((r) => r.id))];
			const [reads, analysis] = await Promise.all([
				Promise.all(ids.map((id) => getJson<RecordRead>(`${base}/records/${encodeURIComponent(id)}`))),
				view.analysis && refetchAnalysis(base, view.analysis),
			]);
			const records = reads.flatMap((r) => [r.record, ...(r.latest ? [r.latest] : [])]);
			const next: ResearchView = { ...view, records, ...(analysis ? { analysis } : {}), snapshotAt: now };
			const report = reportIn(records);
			if (report) next.report = report;
			else delete next.report;
			return next;
		}
		case "context": {
			const list = await getJson<{ records: StoredRecord[]; latest: StoredRecord[] }>(`${base}/records`);
			const latest = new Map(list.latest.map((r) => [r.id, r]));
			const reads: RecordRead[] = list.records.map((record) => ({ record, ...(latest.has(record.id) ? { latest: latest.get(record.id) } : {}) }));
			return { ...view, index: researchIndexOf(reads), snapshotAt: now };
		}
		case "capabilities": {
			const capabilities = await getJson<ResearchCapabilities>(`${base}/capabilities`);
			return { ...view, capabilities, revision: capabilities.revision, snapshotAt: now };
		}
		case "verification_summary": {
			const summary = await getJson<VerificationSummary>(`${base}/verification/summary`);
			return { ...view, summary, revision: summary.revision, snapshotAt: summary.snapshotAt };
		}
		case "map": {
			const focus = view.map?.focus;
			const query = focus ? `?${new URLSearchParams({ focus: focus.id, depth: String(focus.depth) }).toString()}` : "";
			const map = await getJson<ResearchMapView>(`${base}/map${query}`);
			return { ...view, map, notices: map.notices, capabilities: map.capabilities, revision: map.revision, snapshotAt: map.snapshotAt };
		}
		case "quality_summary": {
			const quality = await getJson<QualitySummary>(`${base}/quality/summary`);
			return { ...view, quality, revision: quality.revision, snapshotAt: quality.snapshotAt };
		}
		default:
			return view;
	}
}

/**
 * Renders research_read, research_propose, research_execute_analysis and research_finalize_report: first the server's view saved with
 * the result, labeled with its snapshot time, then the scope's API as it answers when opened, when the page
 * comes back to the foreground and after a local change. Nothing the agent typed is drawn; a quotation is shown
 * exactly as given, and a map is drawn from the server's derivation.
 */
export function Research({ result }: ComponentProps): React.ReactNode {
	const read = readResearchResult(result);
	if (read.state === "running") {
		return (
			<div className="component research-component">
				<p className="hint">处理中…</p>
			</div>
		);
	}
	if (read.state === "failed") return <div className="component error">研究操作失败：{read.message}</div>;
	return <LiveResearchView saved={read.view} />;
}

function LiveResearchView({ saved }: { saved: ResearchView }) {
	const [view, setView] = useState(saved);
	const [live, setLive] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const refresh = useCallback(() => {
		refetchView(saved)
			.then((next) => {
				setView(next);
				setLive(true);
				setError(null);
			})
			.catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
	}, [saved]);
	useEffect(() => {
		refresh();
		const onShow = () => document.visibilityState === "visible" && refresh();
		window.addEventListener(RESEARCH_CHANGED, refresh);
		document.addEventListener("visibilitychange", onShow);
		return () => {
			window.removeEventListener(RESEARCH_CHANGED, refresh);
			document.removeEventListener("visibilitychange", onShow);
		};
	}, [refresh]);
	return <ResearchViewBody view={view} live={live} refreshError={error} />;
}

/** One research view, drawn from the view alone. */
export function ResearchViewBody({ view, live = false, refreshError = null }: { view: ResearchView; live?: boolean; refreshError?: string | null }) {
	const unavailable = Object.entries(view.capabilities.domains).filter(([, c]) => !c.available);
	return (
		<div className="component research-component">
			<div className="research-head">
				<span className="research-title">{sectionLabel(view.section)}</span>
				<span className="hint">
					{scopeLabel(view.scopeRef)} · {live ? "更新于" : "快照"} {timeLabel(view.snapshotAt)}
				</span>
			</div>
			{refreshError && <p className="research-notice error">刷新失败：{refreshError}</p>}
			{view.notices.map((n, i) => (
				<p key={i} className={`research-notice ${n.kind}`}>
					{noticeLabel(n)}
					{n.message ? `：${n.message}` : ""}
				</p>
			))}
			{view.blocked && (
				<div className="research-blocked">
					<h4>{blockedCopy(view.blocked)}</h4>
				</div>
			)}
			{view.map && <ResearchMapPanel map={view.map} scopeRef={view.scopeRef} />}
			{view.evidence && <EvidenceSection summary={view.evidence} />}
			{view.impacts && <ImpactSection items={view.impacts} />}
			{view.quality && <QualitySummaryCard summary={view.quality} />}
			{view.report && <ReportSection report={view.report} />}
			{view.records.length > 0 && <RecordSection records={view.records} scope={view.scopeRef} />}
			{view.analysis && <AnalysisSection analysis={view.analysis} empty={view.section === "analysis_summary"} />}
			{view.index && (
				<div className="research-block">
					<p className="research-meta">{Object.entries(view.index.counts).map(([kind, n]) => `${kindLabel(kind)} ${n}`).join(" · ") || "暂无记录"}</p>
					<ul className="research-list">
						{view.index.recent.map((r) => (
							<li key={r.id}>
								{kindLabel(r.kind)} · {r.title} <span className="hint">r{r.revision} · {recordStatusLabel(r.status)}</span>
							</li>
						))}
					</ul>
				</div>
			)}
			{view.summary && <SummarySection summary={view.summary} />}
			{view.section === "capabilities" && (
				<ul className="research-list">
					{Object.entries(view.capabilities.domains).map(([domain, c]) => (
						<li key={domain}>
							{DOMAIN_LABELS[domain as keyof typeof DOMAIN_LABELS] ?? domain}：{c.available ? "已部署" : `未部署${c.reason ? `（${c.reason}）` : ""}`}
						</li>
					))}
				</ul>
			)}
			{unavailable.length > 0 && view.section !== "capabilities" && (
				<p className="research-meta">未部署：{unavailable.map(([d]) => DOMAIN_LABELS[d as keyof typeof DOMAIN_LABELS] ?? d).join("、")}</p>
			)}
			{view.targets.length > 0 && (
				<p className="research-links">
					{view.targets.map((t, i) => (
						<a key={i} href={t.href} target={t.kind === "artifact" ? "_blank" : undefined} rel="noreferrer" onClick={t.kind === "artifact" ? undefined : (e) => openInApp(e, t.href)}>
							{t.kind === "page" ? "研究记录页" : t.label}
						</a>
					))}
				</p>
			)}
		</div>
	);
}

function locatorLabel(locator: AnchorLocator | undefined): string {
	if (!locator) return "";
	if (locator.type === "lines") return locator.start === locator.end ? `第 ${locator.start} 行` : `第 ${locator.start}–${locator.end} 行`;
	if (locator.type === "extracted") return `第 ${locator.paragraph} 段${locator.page ? `（第 ${locator.page} 页）` : ""}`;
	return "";
}

function EvidenceSection({ summary }: { summary: EvidenceSummary }) {
	return (
		<>
			{summary.needsReview.length > 0 && (
				<div className="research-block">
					<p className="research-subtitle">待复查段落</p>
					<ul className="research-list">
						{summary.needsReview.map((r, i) => (
							<li key={i}>
								<span className={`research-state ${r.status}`}>{impactStatusLabel(r.status)}</span> 草稿 {r.draftId} · 锚点 {r.anchorId}
								<span className="hint"> {r.reason}</span>
							</li>
						))}
					</ul>
				</div>
			)}
			{summary.claims.length === 0 && <p className="hint">暂无主张</p>}
			{summary.claims.map((c) => (
				<ClaimCard key={c.id} claim={c} />
			))}
			{summary.sources.length > 0 && (
				<div className="research-block">
					<p className="research-subtitle">来源</p>
					<ul className="research-list">
						{summary.sources.map((s) => (
							<li key={s.id}>
								{s.title} <span className="hint">r{s.revision} · {sourceStatusLabel(s.sourceStatus)} · 存在性 {existenceLabel(s.existence)}{s.capturedAt ? ` · 快照 ${timeLabel(s.capturedAt)}` : ""}</span>
								{s.limitations.map((l, i) => (
									<span key={i} className="research-limit"> 局限：{l}</span>
								))}
							</li>
						))}
					</ul>
				</div>
			)}
			{summary.drafts.length > 0 && (
				<div className="research-block">
					<p className="research-subtitle">草稿</p>
					<ul className="research-list">
						{summary.drafts.map((d) => (
							<li key={d.id}>
								{d.title ?? d.path ?? d.id} <span className="hint">r{d.revision} · {formatLabel(d.format)} · {anchorStatusLabel(d.anchorStatus)}</span>
								{[...d.limitations, ...d.findings].map((l, i) => (
									<span key={i} className="research-limit"> 局限：{l}</span>
								))}
							</li>
						))}
					</ul>
				</div>
			)}
		</>
	);
}

function ClaimCard({ claim }: { claim: ClaimSummary }) {
	return (
		<div className="research-block research-claim">
			<p className="research-claim-text">{claim.text}</p>
			<p className="research-meta">
				<span className={`research-state ${claim.support}`}>支持度 {supportLabel(claim.support)}</span> · 存在性 {existenceLabel(claim.existence)} · r{claim.revision} · {recordStatusLabel(claim.status)} · {authorityLabel(claim.authority)}
			</p>
			{claim.supportReason && <p className="hint">{claim.supportReason}</p>}
			{claim.assessment && (
				<p className="research-meta">
					评估 r{claim.assessment.revision}：存在性 {existenceLabel(claim.assessment.existence)} · 支持度 {supportLabel(claim.assessment.support)} · 代理评估{claim.assessment.current ? "" : " · 已过期"}
					{claim.assessment.rationale && <span className="hint"> {claim.assessment.rationale}</span>}
				</p>
			)}
			{claim.evidence.map((e) => (
				<div key={e.id} className="research-evidence">
					<blockquote className="research-quote">{e.passage.text}</blockquote>
					<p className="research-meta">
						{readingLabel(e.reading)} · {relationLabel(e.relation)} · {e.sourceTitle} {locatorLabel(e.passage.locator as AnchorLocator)} · {e.quoteCheck.status === "matched" ? "原文已核对" : "未核对原文"} · {sourceStatusLabel(e.sourceStatus)}
						{!e.current && <span className="research-state stale"> 已过期</span>}
					</p>
					{e.conditions && <p className="research-meta">条件：{e.conditions}</p>}
					{e.limitations && <p className="research-meta research-limit">局限：{e.limitations}</p>}
				</div>
			))}
			{claim.anchors.length > 0 && (
				<p className="research-meta">
					草稿锚点：
					{claim.anchors.map((a) => `${a.draftId} · ${a.anchorId}（${anchorStatusLabel(a.anchorStatus)}）`).join("；")}
				</p>
			)}
		</div>
	);
}

function ImpactSection({ items }: { items: ImpactItem[] }) {
	if (items.length === 0) return <p className="hint">暂无受影响段落</p>;
	return (
		<ul className="research-list">
			{items.map((it, i) => (
				<li key={i} className="research-block">
					<p className="research-meta">
						<span className={`research-state ${it.status}`}>{impactStatusLabel(it.status)}</span> 草稿 {it.draftId} r{it.draftRevision} · 锚点 {it.anchorId} · {anchorStatusLabel(it.anchorStatus)}
						{it.movedTo && ` · 现位于${locatorLabel(it.movedTo)}`} · {it.acknowledged ? "已复查" : "待复查"}
					</p>
					<blockquote className="research-quote">{it.quote}</blockquote>
					<p className="hint">{it.reasons.length ? it.reasons.join("；") : it.reason}</p>
				</li>
			))}
		</ul>
	);
}

function recordText(record: StoredRecord): string {
	if (record.unsupported) return record.reason;
	const b = record.body as Record<string, unknown>;
	const input = record.kind === "verification_report" ? (b.input as { path?: unknown } | undefined) : undefined;
	const named = [b.title, b.text, (b.passage as { text?: unknown } | undefined)?.text, input?.path, b.rationale, b.description, b.correction].find((v) => typeof v === "string" && v.trim());
	return typeof named === "string" ? named : "";
}

function RecordSection({ records, scope }: { records: StoredRecord[]; scope: ResearchScopeRef }) {
	return (
		<ul className="research-list">
			{records.map((r) => (
				<li key={`${r.id}:${r.revision}`} className={!r.unsupported && (FEEDBACK_KINDS as readonly string[]).includes(r.kind) ? "research-block" : undefined}>
					{kindLabel(r.kind)} · {recordText(r)}
					<span className="hint">
						{" "}
						r{r.revision} · {recordStatusLabel(r.status)}
						{!r.unsupported && ` · ${authorityLabel(r.authority)} · ${r.origin.actor} · ${timeLabel(r.createdAt)}`}
						{!r.unsupported && r.rejectionReason && ` · 拒绝理由：${r.rejectionReason}`}
					</span>
					{!r.unsupported && DECISION_KINDS.includes(r.kind as DecisionKind) && <DecisionDetail record={r} />}
					{!r.unsupported && Array.isArray((r.body as { limitations?: unknown }).limitations) && ((r.body as { limitations: unknown[] }).limitations as unknown[]).map((l, i) => (
						<span key={i} className="research-limit"> 局限：{String(l)}</span>
					))}
					{!r.unsupported && <FeedbackFields record={r} scope={scope} />}
				</li>
			))}
		</ul>
	);
}

function isFileRef(ref: unknown): ref is VerificationFileRef {
	return Boolean(ref) && typeof ref === "object" && "sourceStatus" in (ref as object) && typeof (ref as { path?: unknown }).path === "string";
}

function refLabel(ref: VerificationCheck["evidenceRefs"][number]): string {
	if (isFileRef(ref)) {
		const state = ref.sourceStatus === "current" ? "" : ref.sourceStatus === "missing" ? " · 不可读取" : " · 与声明不一致";
		return `${ref.path}${ref.sha256 ? ` · SHA-256 ${ref.sha256.slice(0, 12)}` : ""}${state}`;
	}
	if (ref.kind === "record" && ref.id) return `记录 ${ref.id}${ref.revision ? ` r${ref.revision}` : ""}`;
	return JSON.stringify(ref);
}

/** A finalized report's disclosure: how it ran, what was checked and what was not, and which boundaries are only prompts. */
export function ReportSection({ report }: { report: VerificationReport }) {
	const e = report.execution;
	return (
		<div className="research-block research-report">
			<p className="research-meta">
				{report.input.path} · 定稿 {timeLabel(report.snapshotAt)} · r{report.revision}
			</p>
			<p className="research-subtitle">执行方式与重叠</p>
			<ul className="research-list">
				<li>
					执行方式：<span className={`research-state ${e.mode}`}>{EXECUTION_MODE_LABEL[e.mode]}</span>
					{e.mode === "unknown" ? "（无平台可核实的执行凭据）" : "（凭据仅证明派发）"}
				</li>
				<li>声称的执行方式：{e.claimedMode ? `${e.claimedMode}（未核实）` : "未声称"}</li>
				{e.receipts.map((r) => (
					<li key={`${r.kind}:${r.id}`}>
						执行凭据：{RECEIPT_RELATION_LABEL[r.relation]} {r.id}
						{r.model ? ` · 模型 ${r.model}` : ""} · 仅证明派发
					</li>
				))}
				{e.unverifiedRefs.map((u, i) => (
					<li key={i}>
						未核实的执行引用：{typeof u.ref === "string" ? u.ref : JSON.stringify(u.ref)} · {u.reason}
					</li>
				))}
				<li>上下文派发：{CONTEXT_DISPATCH_LABEL[e.contextDispatch]}</li>
				<li>
					角色互读限制：{SIBLING_READ_LABEL[e.siblingReadConstraint]}（{e.siblingReadConstraint}）
				</li>
				<li>错误独立性：未确立（not_established）</li>
				<li>
					模型重叠：{MODEL_OVERLAP_LABEL[e.overlap.modelOverlap]}
					{e.overlap.models.length ? `（${e.overlap.models.join("、")}）` : ""}
				</li>
				<li>
					来源重叠：{SOURCE_OVERLAP_LABEL[e.overlap.sourceOverlap]}
					{e.overlap.sources.length ? `（${e.overlap.sources.join("、")}）` : ""}
				</li>
			</ul>
			<p className="research-subtitle">角色共识与外部核验</p>
			<ul className="research-list">
				<li>
					角色共识（补充项，不计入外部核验）：
					{report.roleConsensus.length
						? report.roleConsensus.map((c) => `${c.id} · ${CHECK_STATUS_LABEL[c.status]}${c.verdict ? ` · ${c.verdict}` : ""}`).join("；")
						: "无"}
				</li>
				<li>
					外部核验：
					{report.externalValidation.length ? report.externalValidation.map((c) => `${c.id} · ${CHECK_TYPE_LABEL[c.type]}${c.verdict ? ` · ${c.verdict}` : ""}`).join("；") : "无"}
				</li>
			</ul>
			<p className="research-subtitle">核验项</p>
			{report.checks.length === 0 && <p className="hint">暂无核验项</p>}
			<ul className="research-list">
				{report.checks.map((c) => (
					<li key={c.id} className={`research-check ${c.status}`}>
						<span>
							{c.id} · {CHECK_TYPE_LABEL[c.type]} · {c.required ? "必需" : "非必需"} ·{" "}
							<span className={`research-state ${c.status}`}>{CHECK_STATUS_LABEL[c.status]}</span>
							{c.added ? " · 平台补记" : ""}
						</span>
						{c.status !== "executed" && <span className="research-meta"> 原因：{c.reason ?? "未说明原因"}</span>}
						{c.method && <span className="research-meta"> · 方法：{c.method}</span>}
						{c.actor && <span className="research-meta"> · 执行者：{c.actor}</span>}
						{c.verdict && <span className="research-meta"> · 结论：{c.verdict}</span>}
						{c.attribution && <span className="research-meta"> · {c.attribution === "person" ? "用户确认的记录" : "代理评估"}</span>}
						{c.quote !== undefined && (
							<>
								<blockquote className="research-quote">{c.quote}</blockquote>
								<span className="research-meta">{c.quoteMatch ? (c.quoteMatch.matched ? "逐字一致" : "未逐字出现") : "未比对"} · 不代表支持论点</span>
							</>
						)}
						{c.type === "source_identity" && <span className="research-meta"> · 来源存在不代表支持论点</span>}
						{c.limitations && <span className="research-meta research-limit"> · 局限：{c.limitations}</span>}
						{c.evidenceRefs.length > 0 && <span className="hint"> · 证据：{c.evidenceRefs.map(refLabel).join("；")}</span>}
					</li>
				))}
			</ul>
			<p className="research-subtitle">未执行项</p>
			<ul className="research-list">
				{report.notRun.length === 0 && <li>无</li>}
				{report.notRun.map((c) => (
					<li key={c.id}>
						{c.id} · {CHECK_TYPE_LABEL[c.type]} · {c.required ? "必需" : "非必需"} · {CHECK_STATUS_LABEL[c.status]}：{c.reason}
					</li>
				))}
			</ul>
			<p className="research-subtitle">问题类型与核验选择</p>
			<ul className="research-list">
				<li>问题类型：{report.questionTypes.join("、")}</li>
				<li>必需核验：{report.requiredChecks.length ? report.requiredChecks.map((t) => CHECK_TYPE_LABEL[t]).join("、") : "无"}</li>
				{report.selection.exclusions.map((x, i) => (
					<li key={`x${i}`}>
						不需要：{x.check}：{x.rationale}
					</li>
				))}
				{report.selection.gaps.map((g, i) => (
					<li key={`g${i}`} className="research-notice stale_view">
						选择缺口：{g}
					</li>
				))}
			</ul>
			<p className="research-subtitle">不确定性</p>
			<ul className="research-list">
				{report.uncertainties.length === 0 && <li>未列出</li>}
				{report.uncertainties.map((u, i) => (
					<li key={i}>{u}</li>
				))}
			</ul>
			<p className="research-subtitle">边界：提示级与平台强制</p>
			<ul className="research-list">
				{report.boundaries.map((b, i) => (
					<li key={i}>
						<span className={`research-state ${b.level}`}>{BOUNDARY_LEVEL_LABEL[b.level]}</span>：{b.detail}
					</li>
				))}
			</ul>
		</div>
	);
}

function SummarySection({ summary }: { summary: VerificationSummary }) {
	return (
		<div className="research-block">
			{summary.reports.length === 0 && <p className="hint">暂无核验报告</p>}
			{summary.unsupported > 0 && <p className="research-notice unsupported_schema">版本不支持：{summary.unsupported} 份</p>}
			<ul className="research-list">
				{summary.reports.map((r) => (
					<li key={r.id}>
						{r.input.path} · 执行方式 {EXECUTION_MODE_LABEL[r.executionMode]} · 问题类型 {r.questionTypes.join("、")}
						<span className="hint">
							{" "}
							已执行 {r.counts.executed} · 未执行 {r.counts.not_run} · 未定论 {r.counts.inconclusive} · 核验失败 {r.counts.failed} · 外部核验 {r.externalValidation} · 必需未执行{" "}
							{r.requiredNotRun}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
